import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { DataTable } from "@/components/ui/data-table";
import {
  PlusCircle,
  Box,
  Edit,
  Trash2,
  Search,
  MoreVertical,
} from "lucide-react";
import { API_ENDPOINTS } from "../../api/header";
import apiClient from "../../api/apiClient";
import { toast } from "sonner";
import { Spinner } from "@/components/ui/spinner";
import Modal from "@/components/ui/modal";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";
import { FilterCard, FilterSearch } from "@/components/ui/filter-card";
import { TableCard } from "@/components/ui/table-card";
import { PageContainer } from "@/components/layout/PageContainer";
import { ReactSelect } from "@/components/ui/react-select";
import RecordNotFound from "@/core/components/RecordNotFound";

const ModelList = () => {
  const queryClient = useQueryClient();
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [modalOpen, setModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    name: "",
    brand_id: "",
    description: "",
    image: "",
    status: "active",
    attributes: "",
  });

  const [brandSearchInput, setBrandSearchInput] = useState("");
  const [brandSearchTerm, setBrandSearchTerm] = useState("");
  const [brandLabels, setBrandLabels] = useState({});

  useEffect(() => {
    const timer = setTimeout(() => {
      setBrandSearchTerm(brandSearchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [brandSearchInput]);

  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    description: "",
    onConfirm: () => {},
  });

  const permissions = useSelector((state) => state.userpermission_data) || [];

  const hasPermission = (perm) => {
    return permissions && permissions.includes(perm);
  };

  // Queries
  const { data: brands = [], isFetching: isBrandsFetching } = useQuery({
    queryKey: ["brands", brandSearchTerm],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.INVENTORY.BRANDS, {
        params: { search: brandSearchTerm || undefined },
      });
      return res.data.data || [];
    },
    enabled: hasPermission("brand read"),
  });
  useEffect(() => {
    if (brands.length > 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setBrandLabels((prev) => {
        const next = { ...prev };
        brands.forEach((b) => {
          next[String(b.id)] = b.name;
        });
        return next;
      });
    }
  }, [brands]);

  const { data: modelData, isLoading: isLoadingModels } = useQuery({
    queryKey: ["models", currentPage, pageSize, searchText],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.INVENTORY.MODELS, {
        params: {
          page: currentPage,
          per_page: pageSize,
          search: searchText || undefined,
        },
      });
      return res.data;
    },
    enabled: hasPermission("brand model read"),
  });
  useEffect(() => {
    if (modelData?.data) {
      const bLabels = {};
      modelData.data.forEach((model) => {
        if (model.brand) {
          bLabels[String(model.brand_id)] = model.brand.name;
        }
      });
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setBrandLabels((prev) => ({ ...prev, ...bLabels }));
    }
  }, [modelData]);

  const models = modelData?.data || [];
  const pagination = {
    current_page: modelData?.meta?.current_page || modelData?.current_page || 1,
    last_page: modelData?.meta?.last_page || modelData?.last_page || 1,
    per_page: modelData?.meta?.per_page || modelData?.per_page || pageSize,
    total: modelData?.meta?.total || modelData?.total || models.length,
  };

  // Mutations
  const saveModelMutation = useMutation({
    mutationFn: async (apiData) => {
      let url, method;
      if (apiData.id) {
        url = API_ENDPOINTS.INVENTORY.MODEL_DETAILS(
          apiData.brand_id,
          apiData.id,
        );
        method = "put";
      } else {
        url = API_ENDPOINTS.INVENTORY.BRAND_MODELS(apiData.brand_id);
        method = "post";
      }
      return await apiClient({ method, url, data: apiData });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["models"] });
      toast.success("Model saved successfully");
      setModalOpen(false);
      setFormData({
        id: null,
        name: "",
        brand_id: "",
        description: "",
        image: "",
        status: "active",
        attributes: "",
      });
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to save model");
    },
  });

  const deleteModelMutation = useMutation({
    mutationFn: async ({ id, brand_id }) => {
      return await apiClient.delete(
        API_ENDPOINTS.INVENTORY.MODEL_DETAILS(brand_id, id),
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["models"] });
      toast.success("Model deleted successfully");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to delete model");
    },
  });

  if (!hasPermission("brand model read")) {
    return <RecordNotFound />;
  }

  const handleSave = async () => {
    if (!formData.name || !formData.brand_id) {
      toast.error("Name and Brand are required fields");
      return;
    }
    saveModelMutation.mutate(formData);
  };

  const handleEdit = async (model) => {
    setFormData({ ...model });
    setModalOpen(true);
  };

  const handleDelete = (id, brand_id) => {
    setConfirmDialog({
      isOpen: true,
      title: "Are you sure?",
      description: "You won't be able to revert this!",
      onConfirm: () => {
        deleteModelMutation.mutate({ id, brand_id });
      },
    });
  };

  const getBrandName = (brandId) => {
    if (brandLabels[String(brandId)]) return brandLabels[String(brandId)];
    const brand = brands.find((b) => b.id === Number(brandId));
    return brand ? brand.name : "Unknown Brand";
  };

  const columns = [
    {
      accessorKey: "name",
      header: "Model",
    },
    {
      accessorKey: "brand_id",
      header: "Brand",
      cell: ({ getValue }) => getBrandName(getValue()),
    },
    {
      accessorKey: "description",
      header: "Description",
      cell: ({ getValue }) => <span>{getValue() || "-"}</span>,
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ getValue }) => (
        <Badge variant={getValue() === "active" ? "success" : "destructive"}>
          {getValue() === "active" ? "Active" : "Inactive"}
        </Badge>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {hasPermission("brand model update") && (
              <DropdownMenuItem onClick={() => handleEdit(row.original)}>
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </DropdownMenuItem>
            )}
            {hasPermission("brand model delete") && (
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onClick={() =>
                  handleDelete(row.original.id, row.original.brand_id)
                }
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <PageContainer>
      <PageHeader>
        <PageHeaderHeading
          title="Models"
          subtitle="Manage your product models"
        />
        <PageHeaderActions>
          {hasPermission("brand model create") && (
            <Button onClick={() => setModalOpen(true)}>
              <PlusCircle className="mr-2 h-4 w-4" /> Add New Model
            </Button>
          )}
        </PageHeaderActions>
      </PageHeader>

      <FilterCard>
        <FilterSearch
          placeholder="Search models..."
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            setCurrentPage(1);
          }}
        />
      </FilterCard>

      <TableCard>
        <DataTable
          columns={columns}
          data={models}
          enableRowSelection={true}
          isLoading={isLoadingModels}
          pageCount={pagination.last_page}
          footerInfo={
            <span className="font-medium text-foreground">
              Total Models: {pagination.total || 0}
            </span>
          }
          pagination={{
            pageIndex: currentPage - 1,
            pageSize: pageSize,
          }}
          onPaginationChange={(updater) => {
            const newState =
              typeof updater === "function"
                ? updater({
                    pageIndex: currentPage - 1,
                    pageSize: pageSize,
                  })
                : updater;
            setCurrentPage(newState.pageIndex + 1);
            setPageSize(newState.pageSize);
          }}
        />
      </TableCard>

      <Modal
        open={modalOpen}
        onOpenChange={setModalOpen}
        title={`${formData.id ? "Edit" : "Add New"} Model`}
        footer={
          <div className="flex justify-end gap-3 -mx-2">
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saveModelMutation.isPending}>
              {saveModelMutation.isPending ? (
                <>
                  <Spinner className="mr-2 h-4 w-4" /> Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
          </div>
        }
      >
        <div className="grid gap-4 py-4">
          <div className="space-y-2">
            <Label>
              Brand <span className="text-destructive">*</span>
            </Label>
            <ReactSelect
              onInputChange={(value) => setBrandSearchInput(value)}
              options={brands.map((brand) => ({
                value: String(brand.id),
                label: brand.name,
              }))}
              value={
                formData.brand_id
                  ? {
                      value: String(formData.brand_id),
                      label:
                        brandLabels[String(formData.brand_id)] ||
                        brands.find(
                          (b) => String(b.id) === String(formData.brand_id),
                        )?.name ||
                        "Select Brand",
                    }
                  : null
              }
              onChange={(opt) =>
                setFormData({
                  ...formData,
                  brand_id: opt ? opt.value : "",
                })
              }
              placeholder="Search Brand..."
              isLoading={isBrandsFetching}
              isClearable
            />
          </div>
          <div className="space-y-2">
            <Label>
              Model Name <span className="text-destructive">*</span>
            </Label>
            <Input
              placeholder="Enter model name"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              autoFocus
            />
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              placeholder="Enter description"
              value={formData.description || ""}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  description: e.target.value,
                })
              }
            />
          </div>
          <div className="space-y-2">
            <Label>Status</Label>
            <Select
              value={formData.status}
              onValueChange={(value) =>
                setFormData({ ...formData, status: value })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Modal>

      {/* Confirmation Dialog */}
      <Modal
        open={confirmDialog.isOpen}
        onOpenChange={(open) =>
          setConfirmDialog({ ...confirmDialog, isOpen: open })
        }
        title={confirmDialog.title}
        footer={
          <div className="flex justify-end gap-3 -mx-2">
            <Button
              variant="outline"
              onClick={() =>
                setConfirmDialog({ ...confirmDialog, isOpen: false })
              }
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                confirmDialog.onConfirm();
                setConfirmDialog({ ...confirmDialog, isOpen: false });
              }}
            >
              Delete
            </Button>
          </div>
        }
      >
        <div className="py-4">
          <p className="text-sm text-muted-foreground font-medium">
            {confirmDialog.description}
          </p>
        </div>
      </Modal>
    </PageContainer>
  );
};

export default ModelList;

